import { useState } from "react";
import { format, parseISO } from "date-fns";
import { Check, Wrench } from "lucide-react";
import { Field, FieldGrid } from "@/components/shared/form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input, NativeSelect } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  useCompleteMaintenanceLog,
  useCreateMaintenanceLog,
  useMachineMaintenanceLogs,
} from "@/features/machines/api";
import type { MachineListRow, MaintenanceKind } from "@/features/machines/types";

export function MachineMaintenanceDialog({
  machine,
  open,
  onOpenChange,
}: {
  machine: MachineListRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: logs = [], isLoading } = useMachineMaintenanceLogs(machine?.id);
  const create = useCreateMaintenanceLog();
  const complete = useCompleteMaintenanceLog();
  const [kind, setKind] = useState<MaintenanceKind>("preventive");
  const [startedAt, setStartedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");

  if (!machine) return null;

  async function onCreate() {
    await create.mutateAsync({
      machine_id: machine!.id,
      kind,
      started_at: startedAt,
      notes,
    });
    setNotes("");
    setKind("preventive");
    setStartedAt(new Date().toISOString().slice(0, 10));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="size-4" />
            Maintenance · {machine.code}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <FieldGrid>
            <Field label="Kind" span={6}>
              <NativeSelect
                value={kind}
                onChange={(e) => setKind(e.target.value as MaintenanceKind)}
              >
                <option value="preventive">Preventive</option>
                <option value="corrective">Corrective</option>
              </NativeSelect>
            </Field>
            <Field label="Started" span={6}>
              <Input type="date" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} />
            </Field>
            <Field label="Notes" span={12}>
              <Textarea
                rows={2}
                className="text-xs"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="What was done / planned…"
              />
            </Field>
          </FieldGrid>
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              disabled={create.isPending}
              onClick={() => void onCreate()}
            >
              {create.isPending ? "Saving…" : "Add log"}
            </Button>
          </div>

          <div className="max-h-56 overflow-auto rounded border">
            {isLoading ? (
              <p className="px-3 py-6 text-center text-xs text-muted-foreground">Loading…</p>
            ) : logs.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-muted-foreground">No logs yet</p>
            ) : (
              <ul className="divide-y text-xs">
                {logs.map((log) => (
                  <li key={log.id} className="flex items-start justify-between gap-2 px-3 py-2">
                    <div className="min-w-0">
                      <div className="font-medium capitalize">
                        {log.kind} · {log.status.replace("_", " ")}
                      </div>
                      <div className="text-muted-foreground">
                        {format(parseISO(log.started_at), "dd MMM yyyy")}
                        {log.completed_at
                          ? ` → ${format(parseISO(log.completed_at), "dd MMM yyyy")}`
                          : ""}
                      </div>
                      {log.notes ? (
                        <div className="mt-0.5 truncate text-muted-foreground">{log.notes}</div>
                      ) : null}
                    </div>
                    {log.status === "open" || log.status === "in_progress" ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        disabled={complete.isPending}
                        onClick={() => void complete.mutateAsync(log)}
                      >
                        <Check />
                        Done
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
